import React from 'react';
import {Image, ImageStyle, Platform, StyleSheet, View, ViewStyle} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {BottomTabHeaderProps} from '@react-navigation/bottom-tabs';
import {DrawerActions} from '@react-navigation/native';
import {ThemeProvider} from '@rn-vui/themed';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';

import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import logoImg from '../images/brand/header-logo.png';
import FilterIconImg from '../images/brand/svg/icons/Filter.svg';
import ShopStoreImg from '../images/brand/svg/icons/My_store_icon_2.svg';
import {AppRoute} from '../models/AppRoute';
import {HeaderConfig} from '../models/navigationTypes';
import * as RootNavigation from '../navigation/RootNavigation';
import WebDesktopTopNavigation from '../navigation/WebDesktopTopNavigation';
import {useGlobalAlert} from '../queries/homepage/useGlobalAlert';
import {
  ABOUT_US,
  ACCOUNT_ACTION_CHOOSER,
  ACCOUNT_COMMUNICATIONS_PREFERENCES,
  BRAND_NAME,
  CHANGE_PASSWORD,
  CONTACT_US,
  CONTACT_US_SUBMIT_TICKET,
  DELETE_ACCOUNT,
  DELIVERY_AND_PICKUP_FAQS,
  DEPARTMENT_NAME,
  DISCOVER_FILTER,
  DISCOVER_SEGMENT,
  EDIT_ACCOUNT,
  EXCLUSIVE_OFFER_LIST,
  EXCLUSIVE_OFFER_NAME,
  FAVOURITE_BRANDS,
  FEED_SEGMENT,
  GIFT_CARDS_HOW_TO_CHECK_BALANCE,
  GIFT_CARDS_HOW_TO_REDEEM,
  GIFT_CARDS_OPTIONS,
  GIFT_CARDS_STORE_ORDER_REVIEW,
  GIFT_CARDS_TERMS_AND_CONDITIONS,
  INFORMATION,
  INFORMATION_CONTENT,
  LOGIN,
  LOGIN_ENTRY,
  MEMBERS_PRICE_LIST,
  NEW_ACCOUNT,
  ORDER_COMPETITIONS,
  ORDER_CONFIRMATION,
  ORDER_SCREEN,
  PAYMENT_METHOD,
  PRIVACY_POLICY,
  PROCESSING_ORDER,
  PROMOTION_NAME,
  PROMOTIONS_CURRENT,
  PROMOTIONS_MAIN,
  PROMOTIONS_WINNERS,
  RESET_PASSWORD,
  RESPONSIBLE_SERVICE,
  RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS,
  RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST,
  RETURNS_AND_EXCHANGES,
  SEARCH,
  SELF_EXCLUSION,
  STORE_DETAILS,
  STORE_NAME,
  STORE_NAME_CARD,
  STORE_NAME_CHECKOUT,
  STORE_SELECTION,
  SUB_DEPARTMENT_NAME,
  SUBURB_SELECTION,
  TERMS_AND_CONDITIONS,
  VIEW_ORDER,
} from '../screens/constants';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import ViewStyled from '../styled-components/ViewStyled';
import rewardsTheme from '../theme/rewardsTheme';
import {getLinkTrail, goBackThroughLinkTrail} from '../utils/navigation';
import {brandRewardsCopy} from '../utils/rewards';

import CartCountBadge from './cart/CartCountBadge';
import StoreName from './checkout/StoreName';
import {isGlobalAlertBannerVisible} from './GlobalAlert/isGlobalAlertBannerVisible';
import Pressable from './ui-controls/PressableWithOpacityFeedback';
import Text from './ui-controls/Text';
import {Icon} from './Icon';
import StoreNameCard from './StoreNameCard';

export type BrandHeaderProps = {
  navigation: BottomTabHeaderProps['navigation'];
  route: BottomTabHeaderProps['route'];
  config: HeaderConfig;
};

const BrandHeader = observer(({navigation, route, config}: BrandHeaderProps) => {
  const {shopSelectionStore, rangeStore, feedStore, globalStore, userStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const isLoggedIn = userStore.isLoggedIn;
  const globalAlert = useGlobalAlert();
  // When the app-root alert strip owns the status-bar inset (native only),
  // drop BrandHeader's paddingTop so we do not double-apply safe area.
  const bannerOwnsStatusBar =
    Platform.OS !== 'web' && isGlobalAlertBannerVisible(globalAlert, globalStore.isHomeRoute);

  const isGiftCardStore = shopSelectionStore.isSelectedStoreGiftCardStore;

  const handleSearchPress = () => {
    if (navigation.getId() === 'SEARCH') {
      navigation.popToTop();
    } else {
      runInAction(() => {
        globalStore.setIsFromNavigationSearch(true);
        navigation.navigate(AppRoute.MAIN, {
          screen: AppRoute.NEW_SEARCH,
        });
      });
    }
  };

  const handleBurgerPress = () => navigation.dispatch(DrawerActions.openDrawer());
  const handleUserPress = () =>
    navigation.navigate(AppRoute.MAIN, {
      screen: isLoggedIn ? AppRoute.ACCOUNT_LOGGED_IN : AppRoute.ACCOUNT_NOT_LOGGED_IN,
    });
  const handleCartPress = () =>
    RootNavigation.navigate(AppRoute.ROOT, {
      screen: AppRoute.MAIN,
      params: {
        screen: AppRoute.CART,
      },
    });
  const handleStoreNamePress = () =>
    navigation.navigate(AppRoute.MAIN, {
      screen: AppRoute.MY_STORE,
    });
  const handleFilterIconPress = () => globalStore.setFiltersVisible(!globalStore.filtersVisible);
  const handleLogoPress = () =>
    navigation.navigate(AppRoute.MAIN, {
      screen: AppRoute.HOME,
    });

  // Universal action, navigation handles it the way it wants too...
  // Like seriously, don't bother trying to be smarter than the guys
  // who wrote the navigation library.
  const backIcon = (props?: {size?: number}) => {
    const navigationState = navigation?.getState();
    const history = navigationState?.history || [];
    const isCatalogueRoute = route?.name === AppRoute.CATALOGUE;
    // Same-route in-page link chain (article -> article). The tab history does
    // not record it — see `applyLinkTrail` in utils/navigation.
    const linkTrail = getLinkTrail(route?.params);

    if (history.length === 1 && !linkTrail && !isGiftCardStore && !isCatalogueRoute) {
      return <ShopStoreImg width={36} height={36} />;
    }

    let handleBackPress = () => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else if (history.length > 1) {
        // Manual tab history navigation
        const previousHistoryItem = history[history.length - 2];
        const routes = navigationState?.routes || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const previousRoute = routes.find((r: any) => r.key === previousHistoryItem.key);

        if (previousRoute) {
          navigation.navigate(previousRoute.name, previousRoute.params);
        } else {
          // Fallback if history logic fails
          navigation.navigate(AppRoute.MAIN, {
            screen: AppRoute.HOME,
          });
        }
      } else {
        // No prior screen and no tab history (deep-link / web-refresh cold start).
        // Bare goBack() no-ops here and strands the user (DIS-1416). Mirror
        // handleLogoPress: route through MAIN to the store-correct home — both HOME
        // (standard stores) and GIFT_CARDS_MAIN (gift-card stores) are screens under
        // MAIN, so this resolves from the flow stacks (Checkout/Promotions/etc.) too,
        // unlike a bare navigate(homeAppRoute) which wouldn't resolve from those.
        navigation.navigate(AppRoute.MAIN, {
          screen: shopSelectionStore.homeAppRoute,
        });
      }
    };

    // Hack: Shop -> "Gift Cards" is not a real "category/department", just a
    // link to the "Gift Cards" promotion. Then, hack making "back" go to
    // "Shop", not Shop -> "Gift Cards"
    if (
      history[history.length - 1]?.key?.startsWith(AppRoute.PROMOTION) &&
      history[history.length - 2]?.key?.startsWith(AppRoute.DEPARTMENT)
    ) {
      handleBackPress = () =>
        navigation.navigate(AppRoute.MAIN, {
          screen: AppRoute.SHOP,
        });
    }

    // Handle deep link to offer detail with no history - go to offers list
    const routes = navigationState?.routes || [];
    const currentIndex = navigationState?.index ?? 0;
    const isOfferDetailWithNoHistory =
      route?.name === AppRoute.EXCLUSIVE_OFFER_DETAIL &&
      (currentIndex === 0 || routes.length <= 1) &&
      history.length <= 1;

    if (isOfferDetailWithNoHistory) {
      handleBackPress = () => navigation.navigate(AppRoute.EXCLUSIVE_OFFER_LIST);
    }

    // DIS-1351: CATALOGUE with nothing to go back to — deep link to
    // /catalogues, or a full page load after the Nexus catalogue download
    // navigated the browser away and the user returned. Tab history resets to
    // a single entry, which used to suppress the back affordance entirely.
    // Mirror the EXCLUSIVE_OFFER_DETAIL special-case above: keep the arrow
    // visible and send the user to Home. canGoBack() (not stack index) is the
    // discriminator because CATALOGUE is a visible tab: in tab state `routes`
    // lists every tab and `index` is the focused tab position, so the
    // stack-index checks used for EXCLUSIVE_OFFER_DETAIL can never match here.
    const isCatalogueWithNoHistory =
      isCatalogueRoute && !navigation.canGoBack() && history.length <= 1;

    if (isCatalogueWithNoHistory) {
      handleBackPress = () =>
        navigation.navigate(AppRoute.MAIN, {
          screen: AppRoute.HOME,
        });
    }

    // Last, so it wins over the special cases above: the user arrived here
    // from another instance of THIS screen via an in-page link, and that
    // origin is not in the tab history.
    if (linkTrail) {
      handleBackPress = () => goBackThroughLinkTrail(linkTrail);
    }

    if (config.back) {
      return (
        <Pressable
          onPress={handleBackPress}
          style={[styles.icon, styles.iconsLeft]}
          testID="header-back-button">
          <Icon
            name="arrow-left-long"
            size={props?.size || 16}
            color={theme.colour.background.button.primary}
          />
        </Pressable>
      );
    } else if (config.shop) {
      return <ShopStoreImg width={36} height={36} />;
    } else {
      return <View style={styles.icon} testID="header-icon" />;
    }
  };

  const filterIcon = () => {
    let showFilter = !!config.filter;

    // On DEPARTMENT screens, only show filter when products are displayed (no sub-department tiles)
    if (showFilter && config.source === DEPARTMENT_NAME) {
      const subDepts = rangeStore.getSubDepartments(rangeStore.selectedDepartment?.id);
      showFilter = subDepts.length === 0;
    }

    // Hide filter icon on product list screens — the StickyFilterBar replaces it
    if (config.source === DEPARTMENT_NAME || config.source === SUB_DEPARTMENT_NAME) {
      showFilter = false;
    }

    return showFilter ? (
      <Pressable onPress={handleFilterIconPress} style={styles.icon}>
        <FilterIconImg width={24} height={24} style={styles.filterIcon} />
      </Pressable>
    ) : (
      <View style={styles.icon} />
    );
  };

  const headerText = () => {
    let text;
    switch (config.source) {
      case DISCOVER_SEGMENT:
        text = route.params?.article?.segment || '';
        break;
      case DISCOVER_FILTER:
        text = feedStore.selectedSegment.label;
        break;
      case FEED_SEGMENT:
        text = feedStore.feedDetail?.segment || route.params.article?.segment || '';
        break;
      case DEPARTMENT_NAME:
        text = rangeStore.selectedDepartment?.name?.toUpperCase() || '';
        break;
      case PROMOTION_NAME:
        text = brandRewardsCopy.promotionsTitle.toUpperCase();
        break;
      case EXCLUSIVE_OFFER_NAME:
        text = 'EXCLUSIVE OFFER';
        break;
      case SUB_DEPARTMENT_NAME:
        text = rangeStore.selectedSubDepartment?.name?.toUpperCase() || '';
        break;
      case STORE_DETAILS:
        text = 'MY STORE';
        break;
      case SUBURB_SELECTION:
        text = 'WELCOME';
        break;
      case STORE_SELECTION:
        text = 'SELECT YOUR STORE';
        break;
      case FAVOURITE_BRANDS:
        text = 'FAVOURITE BRANDS';
        break;
      case BRAND_NAME:
        text = route.params.brand?.brand?.toUpperCase() || '';
        break;
      case SEARCH:
        text = 'SEARCH';
        break;
      case LOGIN:
        text = 'LOG IN';
        break;
      case LOGIN_ENTRY:
        text = 'ENTER EMAIL';
        break;
      case NEW_ACCOUNT:
        text = 'CREATE ACCOUNT';
        break;
      case DELETE_ACCOUNT:
        text = 'DELETE ACCOUNT';
        break;
      case EDIT_ACCOUNT:
        text = 'YOUR DETAILS';
        break;
      case RESET_PASSWORD:
        text = 'RESET PASSWORD';
        break;
      case CHANGE_PASSWORD:
        text = 'CHANGE PASSWORD';
        break;
      case ACCOUNT_COMMUNICATIONS_PREFERENCES:
        text = 'PREFERENCES';
        break;
      case INFORMATION:
        text = 'HELP';
        break;
      case EXCLUSIVE_OFFER_LIST:
        text = brandRewardsCopy.exclusiveOffersTitle.toUpperCase();
        break;
      case MEMBERS_PRICE_LIST:
        text =
          route?.params?.external_sell_type === 'just-for-you'
            ? brandRewardsCopy.memberPrivatePricingTitle.toUpperCase()
            : brandRewardsCopy.memberPublicPricingTitle.toUpperCase();
        break;
      case INFORMATION_CONTENT: {
        switch (route?.params?.target) {
          case ABOUT_US:
            text = 'ABOUT US';
            break;
          case RETURNS_AND_EXCHANGES:
            text = 'RETURNS & EXCHANGES';
            break;
          case DELIVERY_AND_PICKUP_FAQS:
            text = 'DELIVERY & PICKUP FAQs';
            break;
          case TERMS_AND_CONDITIONS:
            text = 'TERMS & CONDITIONS';
            break;
          case PRIVACY_POLICY:
            text = 'PRIVACY POLICY';
            break;
          default:
            text = 'HELP';
        }
        break;
      }
      case CONTACT_US:
        text = 'CONTACT US';
        break;
      case CONTACT_US_SUBMIT_TICKET:
        text = 'SUBMIT A TICKET';
        break;
      case SELF_EXCLUSION:
        text = 'SELF EXCLUSION';
        break;
      case ORDER_SCREEN:
        text = 'RECENT ORDERS';
        break;
      case ACCOUNT_ACTION_CHOOSER:
        text = 'ACCOUNT MANAGEMENT';
        break;
      case VIEW_ORDER:
        text = route.params?.order ? `Order #${route.params?.order?.order_number}` : 'VIEW ORDER';
        break;
      case PAYMENT_METHOD:
        text = 'PAYMENT METHOD';
        break;
      case PROCESSING_ORDER:
        text = 'PROCESSING ORDER';
        break;
      case ORDER_COMPETITIONS:
        text = 'COMPETITION ENTRIES';
        break;
      case ORDER_CONFIRMATION:
        text = 'ORDER CONFIRMATION';
        break;
      case PROMOTIONS_MAIN:
        text = brandRewardsCopy.promotionsTitle.toUpperCase();
        break;
      case PROMOTIONS_CURRENT:
        text = `CURRENT ${brandRewardsCopy.promotionsTitle.toUpperCase()}`;
        break;
      case PROMOTIONS_WINNERS:
        text = 'WINNERS!';
        break;
      case RESPONSIBLE_SERVICE:
        text = 'RESPONSIBLE SERVICE';
        break;
      case RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST:
        text = '3RD PARTY REVIEW REQUEST';
        break;
      case RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS:
        text = 'LIQUOR LICENSING LAWS';
        break;
      case GIFT_CARDS_STORE_ORDER_REVIEW:
        text = 'REVIEW YOUR ORDER';
        break;
      case GIFT_CARDS_TERMS_AND_CONDITIONS:
        text = 'GIFT CARD TERMS OF USE';
        break;
      case GIFT_CARDS_HOW_TO_REDEEM:
        text = 'HOW TO REDEEM A GIFT CARD';
        break;
      case GIFT_CARDS_OPTIONS:
        text = 'GIFT CARDS';
        break;
      case GIFT_CARDS_HOW_TO_CHECK_BALANCE:
        text = 'CHECK A GIFT CARD BALANCE';
        break;
      default:
        text = '';
    }

    if (config.source === STORE_NAME) {
      if (isGiftCardStore) {
        return (
          <View style={styles.centeredView}>
            <Text style={styles.departmentName}>GIFT CARD STORE</Text>
          </View>
        );
      } else {
        return (
          <Pressable onPress={handleStoreNamePress} style={styles.storeView}>
            <Text style={styles.locationUpper}>
              MY STORE <Icon name="chevron-down" size={6} style={styles.chevron} />
            </Text>
            <Text style={[styles.location]} numberOfLines={1} ellipsizeMode="tail">
              {shopSelectionStore.selectedStore?.name || 'Your Store'}
            </Text>
          </Pressable>
        );
      }
    } else {
      return (
        <View style={styles.centeredView}>
          <Text style={styles.departmentName} numberOfLines={1} ellipsizeMode="tail">
            {text}
          </Text>
        </View>
      );
    }
  };

  const getDynamicHeader = () => (
    <View
      testID="brand-header-dynamic-header-parent"
      style={{
        flexDirection: 'row',
        justifyContent: 'center',
        backgroundColor: 'white',
      }}>
      <View
        style={[styles.storeHeader, isWebDesktopScreen && styles.webMaxWidth]}
        testID="brand-header-dynamic-header-store">
        {backIcon()}
        {headerText()}
        {filterIcon()}
      </View>
    </View>
  );
  const insets = useSafeAreaInsets();
  return (
    <>
      <ViewStyled testID="brand-header" style={{paddingTop: bannerOwnsStatusBar ? 0 : insets.top}}>
        <View
          style={{flexDirection: 'row', justifyContent: 'center'}}
          testID="brand-header-outer-container">
          <View
            style={[styles.primary, isWebDesktopScreen && styles.webMaxWidth]}
            testID="brand-header-inner-container">
            <View style={styles.iconsLeft} testID="brand-header-icon-left">
              {!!config.burger && (
                <Pressable
                  hitSlop={50}
                  onPress={handleBurgerPress}
                  testID={`hamburgler-${route.name}`}>
                  <Icon name="bars" size={20} style={styles.icons} />
                </Pressable>
              )}
            </View>
            {isWebDesktopScreen ? (
              <Pressable onPress={handleLogoPress} style={styles.logoContainerWebDesktop}>
                <Image source={logoImg} style={styles.logoImageWebDesktop} resizeMode="contain" />
              </Pressable>
            ) : (
              <Image source={logoImg} style={styles.logoContainer} resizeMode="contain" />
            )}

            <View style={styles.iconsRight} testID="brand-header-icons-right">
              {!config.blinkerMode && (
                <>
                  {!!isWebDesktopScreen &&
                    !!shopSelectionStore.selectedStore &&
                    !shopSelectionStore.isSelectedStoreGiftCardStore &&
                    !shopSelectionStore.ecommerceDisabled && (
                      <Pressable onPress={handleCartPress} testID="brand-header-icons-right-cart">
                        <Icon name="cart-shopping" size={20} style={styles.icons} />
                        <CartCountBadge />
                      </Pressable>
                    )}
                  <Pressable
                    onPress={handleUserPress}
                    style={styles.middleIcon}
                    testID="brand-header-icons-right-user">
                    <Icon
                      name="user"
                      iconStyle={isLoggedIn ? 'solid' : 'regular'}
                      size={20}
                      style={styles.icons}
                    />
                  </Pressable>
                  {!!(
                    config.search &&
                    !isGiftCardStore &&
                    !shopSelectionStore.ecommerceDisabled
                  ) && (
                    <Pressable onPress={handleSearchPress} testID="brand-header-icons-right-search">
                      <Icon name="magnifying-glass" size={20} style={styles.icons} />
                    </Pressable>
                  )}
                </>
              )}
            </View>
          </View>
        </View>
        {!!isWebDesktopScreen &&
          !!shopSelectionStore.selectedStore &&
          !shopSelectionStore.isSelectedStoreGiftCardStore &&
          !config.blinkerMode && (
            <WebDesktopTopNavigation
              storeLoyaltyEnabled={shopSelectionStore.loyaltyEnabled}
              ecommerceDisabled={shopSelectionStore.ecommerceDisabled}
            />
          )}
        {!config.blinkerMode && config.source !== STORE_NAME_CARD && getDynamicHeader()}
      </ViewStyled>
      {config.source === STORE_NAME_CARD && (
        <ThemeProvider theme={rewardsTheme}>
          <StoreNameCard
            onStoreNamePress={handleStoreNamePress}
            storeName={shopSelectionStore.selectedStore?.name || 'Your Store'}
            canDeliver={shopSelectionStore.canDeliver}
            canPickup={shopSelectionStore.canPickup}
          />
        </ThemeProvider>
      )}
      {config.source === STORE_NAME_CHECKOUT && (
        <ThemeProvider theme={rewardsTheme}>
          <StoreName
            icon={backIcon({back: true, size: 20})}
            storeName={shopSelectionStore.selectedStore?.name || 'Your Store'}
          />
        </ThemeProvider>
      )}
    </>
  );
});

export default BrandHeader;

const styles = StyleSheet.create({
  webMaxWidth: {
    maxWidth: '1320px',
  },
  primary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  logoContainer: {
    flex: 6,
    height: 44,
  },
  logoContainerWebDesktop: {
    flex: 6,
    height: 64,
    cursor: 'pointer',
  } as ViewStyle,
  logoImageWebDesktop: {
    width: '100%',
    height: '100%',
  } as ImageStyle,
  icon: {
    flex: 1,
    height: '100%',
    color: theme.colour.icon.general.primary,
  },
  iconsLeft: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
  },
  iconsRight: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    flex: 1,
  },
  middleIcon: {
    marginLeft: 8,
    marginRight: 8,
  },
  icons: {
    color: theme.colour.icon.header.primary,
  },
  // Store and filter line
  storeHeader: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 4,
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: 'grey',
    backgroundColor: 'white',
    height: 40,
    marginHorizontal: 16, // TODO: padding on mobile
  },
  storeView: {
    flex: 8,
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  centeredView: {
    flex: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  locationUpper: {
    fontSize: 8,
    color: 'grey',
  },
  chevron: {
    color: 'grey',
  },
  location: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  departmentName: {
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    fontSize: 20,
  },
  filterIcon: {
    marginTop: 4,
  },
});
